"use client";

import ViewportHeightWrapper from "@/components/ViewportHeightWrapper";
import { useDebounce } from "@/hooks/useDebounce";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useKeyPressed } from "@/hooks/useKeyPressed";
import { useOnClickOutside } from "@/hooks/useOnClickOutside";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { useLocale } from "next-intl";
import Image from "next/image";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Spinner from "../ui/Spinner";

const MIN_QUERY_LEN = 2;

const AutoComplete = ({
  id,
  label,
  value,
  onSelect,
  searchFunction,
  placeholder,
  isFromDate,
  error,
  latestFlightSearches,
  popularCities,
  nearestAirports,
  isMultiCity = false,
  onSelectLatestSearch,
  scrollToForm,
  viewportHeightWrapperActive,
  disableAnimation = true,
  disabled = false,
  isInitialLoading = false,
}) => {
  const isMobile = useIsMobile();
  const t = useTranslationDetector("flightSearch");

  const [internalValue, setInternalValue] = useState(value || "");
  const [lastExternalValue, setLastExternalValue] = useState(value);
  const lockedScrollYRef = useRef(0);

  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isMobileFocused, setIsMobileFocused] = useState(false);

  // null => default content, [] => no results, [..] => results
  const [searchResults, setSearchResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [shouldRenderPortal, setShouldRenderPortal] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const [shouldRenderWebDropdown, setShouldRenderWebDropdown] = useState(false);
  const [isWebDropdownAnimating, setIsWebDropdownAnimating] = useState(false);

  const escapePressed = useKeyPressed("Escape");
  const debouncedValue = useDebounce(internalValue, 300);

  const ref = useRef(null);
  const portalRootRef = useRef(null);

  const mobileRef = useRef(null);
  const inputRef = useRef(null);
  const blurTimeoutRef = useRef(null);
  const locale = useLocale();
  const hiddenRef = useRef(null);

  const lastQueryRef = useRef("");

  //  kritik: sadece kullanıcı gerçekten yazdıysa arama yap
  const hasUserTypedRef = useRef(false);

  //  mobil drawer seçim yapılmadan kapatılırsa desktop input'u bu değere geri dönecek
  const mobileDrawerOpenedWithValueRef = useRef("");

  const handleClickOutside = useCallback(() => {
    if (isMobile && shouldRenderPortal) return;
    if (!isMobile && isOpen) {
      setIsFocused(false);
      setIsOpen(false);
    }
  }, [isMobile, isOpen, shouldRenderPortal]);

  useEffect(() => {
    if (escapePressed && isOpen) {
      setIsFocused(false);
      setIsOpen(false);
    }
  }, [escapePressed, isOpen]);

  useOnClickOutside(ref, handleClickOutside);

  //  Portal ve animasyon kontrolü (disableAnimation ile)
  useEffect(() => {
    if (isOpen && isMobile) {
      setShouldRenderPortal(true);

      if (disableAnimation) {
        // Animasyon kapalıysa direkt aç
        setIsAnimating(true);
      } else {
        // Animasyon açıksa normal flow
        const timer = setTimeout(() => setIsAnimating(true), 10);
        return () => clearTimeout(timer);
      }
    }

    if (!isOpen && isMobile) {
      setIsAnimating(false);

      if (disableAnimation) {
        // Animasyon kapalıysa direkt kaldır
        setShouldRenderPortal(false);
      } else {
        // Animasyon açıksa bekle
        const timer = setTimeout(() => setShouldRenderPortal(false), 300);
        return () => clearTimeout(timer);
      }
    }
  }, [isOpen, isMobile, disableAnimation]);

  // Web dropdown animasyon kontrolü
  useEffect(() => {
    if (isOpen && !isMobile) {
      setShouldRenderWebDropdown(true);
      const timer = setTimeout(() => setIsWebDropdownAnimating(true), 10);
      return () => clearTimeout(timer);
    }

    if (!isOpen && !isMobile) {
      setIsWebDropdownAnimating(false);
      const timer = setTimeout(() => setShouldRenderWebDropdown(false), 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isMobile]);

  /* =========================
     BODY SCROLL LOCK (Fix)
  ========================== */
  useEffect(() => {
    if (!isMobile || !shouldRenderPortal) return;

    const scrollY = window.scrollY || document.documentElement.scrollTop || 0;
    lockedScrollYRef.current = scrollY;

    const prevOverflow = document.body.style.overflow;
    const prevPosition = document.body.style.position;
    const prevTop = document.body.style.top;
    const prevWidth = document.body.style.width;

    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";

    const preventTouchMove = (e) => {
      const root = portalRootRef.current;
      if (root && root.contains(e.target)) return;
      e.preventDefault();
    };
    document.addEventListener("touchmove", preventTouchMove, {
      passive: false,
    });

    return () => {
      document.removeEventListener("touchmove", preventTouchMove);

      const prevScrollBehavior = document.documentElement.style.scrollBehavior;
      document.documentElement.style.scrollBehavior = "auto";

      document.body.style.overflow = prevOverflow;
      document.body.style.position = prevPosition;
      document.body.style.top = prevTop;
      document.body.style.width = prevWidth;

      const y = lockedScrollYRef.current;

      window.scrollTo(0, y);
      requestAnimationFrame(() => {
        window.scrollTo(0, y);
        requestAnimationFrame(() => {
          document.documentElement.style.scrollBehavior = prevScrollBehavior;
        });
      });
    };
  }, [isMobile, shouldRenderPortal]);

  /* =========================
     INPUT DIŞI TOUCH -> BLUR (Portal içinde)
  ========================== */
  useEffect(() => {
    if (!isMobile || !shouldRenderPortal) return;

    const root = portalRootRef.current;
    const input = mobileRef.current;

    if (!root || !input) return;

    const onTouchStart = (e) => {
      if (document.activeElement !== input) return;
      if (input.contains(e.target)) return;
      input.blur();
    };

    root.addEventListener("touchstart", onTouchStart, true);
    return () => root.removeEventListener("touchstart", onTouchStart, true);
  }, [isMobile, shouldRenderPortal]);

  const processAirportList = (airports) => {
    const result = [];
    const cityMap = new Map();

    (airports || []).forEach((airport) => {
      const city = airport.cityName;
      if (!cityMap.has(city)) cityMap.set(city, []);
      cityMap.get(city).push(airport);
    });

    (airports || []).forEach((airport) => {
      const cityAirports = cityMap.get(airport.cityName) || [];
      const hasCityEntry = cityAirports.some((a) => a.isCity);
      const subAirportsCount = cityAirports.filter((a) => !a.isCity).length;

      const isIndented = hasCityEntry && !airport.isCity;
      const hasSubAirports = airport.isCity && subAirportsCount > 0;

      let updatedValue = airport.value;
      if (hasSubAirports) {
        updatedValue = `${airport.value
          .replace(/\s*\([A-Z]{3}\)\s*$/, "")
          .trim()} - ${t("allAirports")}`;
      }

      result.push({
        ...airport,
        value: updatedValue,
        isIndented,
        hasSubAirports,
      });
    });

    return result;
  };

  //  min length + query normalize
  const handleSearch = useCallback(
    async (query) => {
      const q = (query || "").trim();

      if (!searchFunction || q.length < MIN_QUERY_LEN) {
        setSearchResults(null);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        lastQueryRef.current = q;

        const results = await searchFunction(q);

        if (lastQueryRef.current === q) {
          setSearchResults(processAirportList(results));
        }
      } catch (err) {
        if (lastQueryRef.current === q) {
          setSearchResults(null);
        }
        // eslint-disable-next-line no-console
        console.error("Search error:", err);
      } finally {
        if (lastQueryRef.current === q) setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchFunction, t],
  );

  //  user typing flag
  const handleChange = useCallback(
    (e) => {
      if (disabled) return;
      hasUserTypedRef.current = true;
      setInternalValue(e.target.value);
    },
    [disabled],
  );

  // External value sync: bunu "selected value" kabul ediyoruz => user typed = false
  useEffect(() => {
    if (value !== lastExternalValue) {
      hasUserTypedRef.current = false;

      setInternalValue(value || "");
      setLastExternalValue(value);

      setSearchResults(null);
      setIsLoading(false);
    }
  }, [value, lastExternalValue]);

  //  debounce search sadece user typed ise çalışır
  useEffect(() => {
    if (!hasUserTypedRef.current) return;

    const q = (debouncedValue || "").trim();

    if (q.length >= MIN_QUERY_LEN) {
      handleSearch(q);
    } else {
      setSearchResults(null);
      setIsLoading(false);
    }
  }, [debouncedValue, handleSearch]);

  const onSelectItem = useCallback(
    (item) => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }

      // seçim yaptı => bu artık "query" değil "selected value"
      hasUserTypedRef.current = false;

      if (
        isMultiCity &&
        item?.departureCity &&
        item?.returnCity &&
        onSelectLatestSearch
      ) {
        const selectedCity = isFromDate ? item.departureCity : item.returnCity;
        const selectedValue = selectedCity.value;

        setInternalValue(selectedValue);
        setLastExternalValue(selectedValue);
        setSearchResults(null);
        setIsFocused(false);
        setIsOpen(false);
        onSelectLatestSearch(item);
        return;
      }

      if (isMultiCity && item?.departureCity && item?.returnCity) {
        const selectedCity = isFromDate ? item.departureCity : item.returnCity;
        const selectedValue = selectedCity.value;

        setInternalValue(selectedValue);
        setLastExternalValue(selectedValue);
        setSearchResults(null);
        setIsFocused(false);
        setIsOpen(false);
        onSelect(selectedCity);
        return;
      }

      const selectedValue =
        item?.departureCity && isFromDate
          ? item.departureCity.value
          : item?.returnCity && !isFromDate
            ? item.returnCity.value
            : item.value;

      setInternalValue(selectedValue);
      setLastExternalValue(selectedValue);

      setSearchResults(null);
      setIsFocused(false);
      setIsOpen(false);

      onSelect(item);
    },
    [onSelect, isFromDate, isMultiCity, onSelectLatestSearch],
  );

  const handlePortalClose = useCallback(() => {
    // Drawer seçim yapılmadan kapatıldı → desktop input değerini drawer açılmadan önceki haline getir
    const valueToRestore =
      mobileDrawerOpenedWithValueRef.current ?? lastExternalValue ?? "";
    setInternalValue(valueToRestore);
    if (mobileRef.current) mobileRef.current.value = "";
    hasUserTypedRef.current = false;
    setIsFocused(false);
    setIsOpen(false);
  }, [lastExternalValue]);

  const onFocus = useCallback(
    (e) => {
      if (disabled) return;
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }

      if (scrollToForm) {
        const inputRect = inputRef.current.getBoundingClientRect();
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        const targetPosition = inputRect.top + scrollTop - 200;

        window.scrollTo({
          top: targetPosition,
          behavior: "smooth",
        });
      }

      setIsFocused(true);
      setIsOpen(true);

      // seçili text'i komple seç => ilk tuş basınca "a" gerçekten "a" olur
      e.target.select();
    },
    [scrollToForm, disabled],
  );

  const clearMobileInput = useCallback(() => {
    // kullanıcı aksiyonu; yazmaya başlayınca true olacak
    hasUserTypedRef.current = false;

    setInternalValue("");
    setSearchResults(null);
    setIsLoading(false);

    if (mobileRef.current) mobileRef.current.value = "";
    mobileRef.current?.focus();
  }, []);

  const containerClasses = useMemo(
    () =>
      clsx(
        "border-primary-weak h-[68px] border-b duration-300 ease-in-out md:h-[66px] md:rounded-lg md:border-0",
        disabled && "pointer-events-none cursor-not-allowed opacity-70",
        !disabled && isFocused && !error && "md:ring-primary md:ring-2",
        !disabled && error && "rounded-lg ring-2 ring-red-500",
      ),
    [isFocused, error, disabled],
  );

  const labelClasses = useMemo(
    () =>
      clsx(
        "absolute left-[18px] bg-white font-medium transition-all duration-300 ease-in-out md:left-[21px]",
        error ? "text-red-500" : "text-foreground-muted",
        isFocused || internalValue || (error && isMobile)
          ? "top-[14.5px] text-xs"
          : "top-1/2 -translate-y-1/2 text-base",
      ),
    [isFocused, internalValue, error, isMobile],
  );

  const inputClasses = useMemo(
    () =>
      clsx(
        "selection:bg-secondary-weak placeholder:text-foreground-muted h-full w-full truncate rounded-lg border-0 px-[18px] font-semibold transition-all duration-300 ease-in-out placeholder:font-normal focus:ring-0 focus:outline-none md:px-[21px]",
        isFocused || internalValue
          ? "pt-[32.5px] pb-[12px] md:pt-[31.5px] md:pb-[13.5px]"
          : "py-[12px] md:py-[22.5px]",
      ),
    [isFocused, internalValue],
  );

  const mobileContainerClasses = useMemo(
    () =>
      clsx(
        "flex h-[56px] items-center justify-between gap-4 rounded-lg border-2 px-4 transition-all duration-300 ease-in-out",
        error && "border-red-500",
        !error && isMobileFocused && "border-primary",
        !error && !isMobileFocused && "border-primary-weak",
        error && isMobileFocused && "border-red-500",
      ),
    [isMobileFocused, error],
  );

  const mobileIconClasses = useMemo(
    () =>
      clsx(
        "text-xl transition-all duration-300 ease-in-out",
        error
          ? "text-red-500"
          : isMobileFocused
            ? "text-primary"
            : "text-foreground-subtle",
      ),
    [isMobileFocused, error],
  );

  //  Animasyon devre dışı kontrolü eklendi
  const portalContainerClasses = useMemo(() => {
    if (disableAnimation) {
      return "fixed inset-0 z-50 flex flex-col bg-white";
    }

    return clsx(
      "fixed inset-0 z-50 flex flex-col bg-white",
      "transform transition-all duration-300",
      isAnimating ? "translate-y-0 ease-out" : "translate-y-full ease-in",
    );
  }, [isAnimating, disableAnimation]);

  const webDropdownClasses = useMemo(() => {
    return clsx(
      "shadow-xxl absolute top-full z-50 mt-2 w-[475px] rounded-lg bg-white p-5",
      "origin-top transform transition-all duration-200 ease-out",
      isWebDropdownAnimating
        ? "translate-y-0 scale-100 opacity-100"
        : "-translate-y-2 scale-95 opacity-0",
      locale === "ar" ? "right-0" : "left-0",
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWebDropdownAnimating]);

  const handleBlur = useCallback(() => {
    if (isMobile && shouldRenderPortal) return;

    blurTimeoutRef.current = setTimeout(() => {
      setIsFocused(false);
      setIsOpen(false);
    }, 150);
  }, [isMobile, shouldRenderPortal]);

  const mobilInputFocus = () => {
    if (!isMobile) return;
    mobileRef.current?.select();
    setIsMobileFocused(true);
  };

  const mobilInputBlur = () => {
    if (!isMobile) return;
    setIsMobileFocused(false);
  };

  //  Drawer'ın kapanma animasyonu bitmediyse açılmasını engelle
  const mobileDrawerTrigger = () => {
    if (disabled) return;
    // Drawer hala kapanıyor mu kontrol et (animasyon açıksa)
    if (!disableAnimation && shouldRenderPortal && !isAnimating) {
      // Kapanma animasyonu devam ediyor, açmayı engelle
      return;
    }

    // drawer açıldığında mevcut değeri sakla; kullanıcı seçim yapmadan kapatırsa buna geri dönülecek
    mobileDrawerOpenedWithValueRef.current = internalValue;

    // drawer açılırken "selected" modundayız; kullanıcı yazınca true olacak
    hasUserTypedRef.current = false;
    setSearchResults(null);
    setIsLoading(false);
    setIsOpen(true);
  };

  useEffect(() => {
    if (isMobile && isOpen && !isAnimating) {
      hiddenRef.current?.focus();
      setTimeout(
        () => {
          mobileRef.current?.focus();
        },
        disableAnimation ? 100 : 500,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, isOpen, isAnimating]);

  const renderSearchResults = useCallback(() => {
    const q = (debouncedValue || "").trim();

    // no results sadece: user typed + min len + loading değil + gerçekten [] döndü
    const shouldShowNoResults =
      hasUserTypedRef.current &&
      q.length >= MIN_QUERY_LEN &&
      !isLoading &&
      Array.isArray(searchResults) &&
      searchResults.length === 0;

    return searchResults === null ? (
      <div className="flex flex-col gap-5 text-sm">
        {latestFlightSearches && (
          <div className="flex flex-col items-start justify-between gap-2">
            <span className="text-center text-gray-500">
              {t("recentSearches")}
            </span>
            <div className="flex w-full flex-col items-start justify-between gap-4">
              {latestFlightSearches.map((search, index) => (
                <button
                  className="flex w-full cursor-pointer items-start justify-between gap-6"
                  onClick={() => onSelectItem(search)}
                  key={index}
                >
                  <div className="flex items-center gap-4">
                    <FontAwesomeIcon
                      icon={byPrefixAndName.far["clock-rotate-left"]}
                    />
                    <div className="flex flex-col gap-1 text-left">
                      <div className="flex items-center justify-start gap-[6px] font-semibold">
                        <span>
                          {search.departureCity.value.split("-")[0].trim()}
                        </span>
                        {search.isOneWay ? (
                          <FontAwesomeIcon
                            icon={byPrefixAndName.fak["half-right-arrow"]}
                          />
                        ) : (
                          <FontAwesomeIcon
                            icon={byPrefixAndName.fak["half-round-trip-arrows"]}
                          />
                        )}

                        <span>
                          {search?.returnCity?.value?.split("-")[0]?.trim()}
                        </span>
                      </div>
                      <div className="text-foreground-muted">
                        {search.isOneWay
                          ? search.departureDate
                          : `${search.departureDate} - ${search.returnDate}`}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 text-sm">
                    {search.departureCity.code}-{search.returnCity.code}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {nearestAirports && (
          <div className="flex flex-col items-start justify-between gap-2">
            <span className="text-center text-gray-500">
              {t("nearbyAirports")}
            </span>
            <div className="flex w-full flex-col gap-3">
              {nearestAirports.map((item, index) => (
                <button
                  className="flex cursor-pointer items-start justify-between gap-6"
                  key={index}
                  onClick={() => onSelectItem(item.AutoCompleteResult)}
                >
                  <div className="flex items-center gap-4 text-left">
                    <FontAwesomeIcon
                      icon={byPrefixAndName.fak["new-flight"]}
                      className="text-lg"
                    />
                    <div className="flex flex-col items-start gap-1">
                      <div className="flex items-center justify-start gap-[6px] font-semibold">
                        {item?.AutoCompleteResult?.value}
                      </div>
                      <div className="text-foreground-muted">
                        {item?.AutoCompleteResult?.label}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">{item.code}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {popularCities && (
          <div className="flex flex-col items-start justify-between gap-2">
            <span className="text-center text-gray-500">
              {t("popularCities")}
            </span>
            <div className="flex w-full flex-wrap gap-3">
              {popularCities.map((item, index) => (
                <button
                  key={index}
                  className="border-border h-[38px] cursor-pointer rounded-lg border px-3 text-base"
                  onClick={() => onSelectItem(item.AutoCompleteResult)}
                >
                  {item.CityName}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    ) : shouldShowNoResults ? (
      <div className="flex flex-col items-center justify-center gap-3 p-10 text-sm">
        <Image
          src="/images/svg/telescope.svg"
          alt="no-results"
          width={60}
          height={60}
        />
        <div className="flex flex-col gap-2">
          <span className="text-center text-lg font-semibold">
            {t("noResultsFound")}
          </span>
          <span className="text-center text-sm">
            {t("changeSearchCriteria")}
          </span>
        </div>
      </div>
    ) : (
      <div className="flex flex-col gap-4 text-sm">
        {searchResults.map((searchResult) => (
          <button
            className={clsx(
              "flex cursor-pointer items-start justify-between gap-6",
              searchResult.isIndented && "pl-8",
            )}
            key={searchResult.id}
            onClick={() => onSelectItem(searchResult)}
          >
            <div className="flex items-center gap-4 text-left">
              {searchResult.isCity ? (
                <FontAwesomeIcon icon={byPrefixAndName.far["location-dot"]} />
              ) : (
                <FontAwesomeIcon
                  icon={byPrefixAndName.fak["new-flight"]}
                  className="text-lg"
                />
              )}
              <div className="flex flex-col items-start gap-1">
                <div className="flex items-center justify-start gap-[6px] font-semibold">
                  {searchResult.hasSubAirports
                    ? searchResult.value.split("-")[0].trim()
                    : `${searchResult.isIndented ? "" : `${searchResult.cityName} - `}${searchResult?.airportName?.replace(/\s*\([^)]*\)/g, "")}`}
                </div>
                <div className="text-foreground-muted">
                  {searchResult.hasSubAirports
                    ? t("allAirports")
                    : searchResult?.countryName}
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-2">{searchResult.code}</div>
          </button>
        ))}
      </div>
    );
  }, [
    debouncedValue,
    isLoading,
    latestFlightSearches,
    nearestAirports,
    popularCities,
    searchResults,
    t,
    onSelectItem,
  ]);

  return (
    <>
      <div className="relative w-full" ref={ref}>
        <input
          ref={hiddenRef}
          className="sr-only"
          tabIndex={-1}
          aria-hidden="true"
        />

        <div className={containerClasses} aria-disabled={disabled}>
          <div className="relative h-full">
            {label && (
              <label htmlFor={id} className={labelClasses}>
                {label}
              </label>
            )}

            {isMobile && (
              <button
                className="absolute top-0 left-0 z-10 h-full w-full bg-transparent"
                type="button"
                onClick={mobileDrawerTrigger}
              />
            )}

            <input
              ref={inputRef}
              type="text"
              value={internalValue}
              id={id}
              onChange={handleChange}
              className={inputClasses}
              autoComplete="off"
              placeholder={isFocused ? placeholder : ""}
              onBlur={handleBlur}
              onFocus={onFocus}
              readOnly={isMobile || disabled}
              aria-disabled={disabled}
            />

            {(isInitialLoading || isLoading) && (
              <Spinner className="text-primary absolute top-1/2 right-4 h-4 w-4 -translate-y-1/2 animate-spin" />
            )}
          </div>
        </div>

        {error && (
          <p className="text-danger absolute -mt-[17px] flex items-center gap-1 pl-4 text-xs md:mt-1">
            <FontAwesomeIcon icon={byPrefixAndName.far["circle-exclamation"]} />
            <span>{error}</span>
          </p>
        )}

        {!isMobile && shouldRenderWebDropdown && (
          <div className={webDropdownClasses}>
            <ViewportHeightWrapper
              defaultMaxHeight={460}
              bottomPadding={32}
              enabled={viewportHeightWrapperActive}
            >
              {renderSearchResults()}
            </ViewportHeightWrapper>
          </div>
        )}
      </div>

      {/* Mobile portal */}
      {shouldRenderPortal &&
        isMobile &&
        createPortal(
          <div ref={portalRootRef} className={portalContainerClasses}>
            <div className="flex shrink-0 items-center justify-between px-4 py-5">
              <span className="w-full text-center text-lg font-semibold">
                {label}
              </span>
              <button onClick={handlePortalClose} className="self-end">
                <FontAwesomeIcon
                  icon={byPrefixAndName.far["xmark"]}
                  className="text-2xl"
                />
              </button>
            </div>

            <div className="flex-1 touch-pan-y overflow-y-auto overscroll-contain px-4 pb-4">
              <div className="flex flex-col gap-6">
                <div className={mobileContainerClasses}>
                  <FontAwesomeIcon
                    icon={byPrefixAndName.far["search"]}
                    className={mobileIconClasses}
                  />

                  <input
                    type="text"
                    onChange={handleChange}
                    onFocus={mobilInputFocus}
                    onBlur={mobilInputBlur}
                    placeholder={placeholder}
                    ref={mobileRef}
                    className="selection:bg-secondary-weak w-3/4 border-0 px-0 py-3 focus:ring-0 focus:outline-none"
                    autoComplete="off"
                  />

                  <div className="flex h-6 w-6 items-center justify-center">
                    {isLoading ? (
                      <Spinner className="text-primary h-4 w-4 animate-spin" />
                    ) : mobileRef.current?.value ? (
                      <button
                        onClick={clearMobileInput}
                        className="bg-background-400 text-foreground-muted flex h-6 w-6 items-center justify-center overflow-hidden rounded-full"
                      >
                        <FontAwesomeIcon
                          icon={byPrefixAndName.far["xmark"]}
                          className="text-sm"
                        />
                      </button>
                    ) : null}
                  </div>
                </div>

                {renderSearchResults()}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default AutoComplete;
